import { useFeatureFlag } from '@artic/ui/feature-flags';
import { Skeleton } from '@artic/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@artic/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';

import { ProductBillOfMaterialsTab } from '../../features/bill-of-materials/product-bill-of-materials-tab';
import { ProductContainerSelections } from '../../features/products/product-container-selections';
import { ProductRunSheetPanel } from '../../features/products/product-run-sheet-panel';
import { productDetailQueryOptions } from '../../features/products/queries';
import {
  importDialogParser,
  productDetailTabParser,
  productDetailTabSearchValue,
  resolveProductDetailTab,
  runSheetVersionParser,
  type ProductDetailTab,
} from '../../features/products/search';

export const Route = createFileRoute('/products/$productId')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { productId } = match.params as { productId: string };
      const data = queryClient.getQueryData(productDetailQueryOptions(productId).queryKey);
      return data?.product_name ?? null;
    },
  },
  component: ProductDetailView,
});

function ProductDetailView() {
  const { productId } = Route.useParams();
  const { t } = useTranslation('common');
  const billOfMaterialsEnabled = useFeatureFlag('bill_of_materials');
  const [tab, setTab] = useQueryState('tab', productDetailTabParser);
  const [version, setVersion] = useQueryState('run_sheet_version', runSheetVersionParser);
  const [importOpen, setImportOpen] = useQueryState('import', importDialogParser);
  const { data: product, isLoading, isError } = useQuery(productDetailQueryOptions(productId));

  if (isError) {
    return (
      <div className="text-destructive py-10 text-center text-sm">
        Failed to load product. Please try again.
      </div>
    );
  }

  if (isLoading || !product) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48 rounded" />
        <Skeleton className="h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold">{product.product_name}</h1>
        <p className="text-muted-foreground mt-1 font-mono text-sm">{product.product_key}</p>
      </div>

      {product.product_description && (
        <div>
          <h2 className="mb-1 text-sm font-medium">Description</h2>
          <p className="text-muted-foreground text-sm">{product.product_description}</p>
        </div>
      )}

      <Tabs
        value={resolveProductDetailTab(tab, version, billOfMaterialsEnabled)}
        onValueChange={(next) =>
          void setTab(productDetailTabSearchValue(next as ProductDetailTab, version))
        }
      >
        <TabsList variant="line">
          <TabsTrigger value="container-selections">Container Selections</TabsTrigger>
          <TabsTrigger value="run-sheet">{t('terms.runSheet')}</TabsTrigger>
          {billOfMaterialsEnabled && (
            <TabsTrigger value="bill-of-materials">{t('terms.billOfMaterials')}</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="container-selections">
          <ProductContainerSelections product={product} />
        </TabsContent>
        <TabsContent value="run-sheet">
          <ProductRunSheetPanel
            productId={productId}
            product={product}
            version={version}
            onVersionChange={(next) => void setVersion(next)}
          />
        </TabsContent>
        {billOfMaterialsEnabled && (
          <TabsContent value="bill-of-materials">
            <ProductBillOfMaterialsTab
              productId={productId}
              importOpen={importOpen}
              onImportOpenChange={(open) => void setImportOpen(open ? true : null)}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
